import ExcelJS from 'exceljs';
import buildReportFilename from './reportFilename';

// WM theme colors (matches importTemplateBuilder.js)
const THEME = {
  headerFill: 'FF1A5568',
  headerFont: 'FFFFFFFF',
  headerBorder: 'FF134452',
  titleColor: 'FF1A5568',
  zebraFill: 'FFF3F4F6',
  borderColor: 'FFE5E7EB'
};

/**
 * Generate and download an Excel report with WM branding.
 */
const exportReportExcel = async ({
  config,
  columns,
  data,
  parameters,
  onProgress
}) => {
  try {
    if (onProgress) onProgress('Generating Excel...');

    const wb = new ExcelJS.Workbook();
    wb.creator = config.branding?.companyName || 'Wunderlich Malec Engineering';
    wb.created = new Date();

    const sheetName = config.reportName.replace(/[/\\?*[\]]/g, '').slice(0, 31);
    const ws = wb.addWorksheet(sheetName, {
      views: [{ state: 'frozen', ySplit: 0 }] // will update after header rows
    });

    let currentRow = 1;

    // ===== Branding Header Rows =====

    // Row 1: Company name
    const companyCell = ws.getCell(currentRow, 1);
    companyCell.value = config.branding?.companyName || 'Wunderlich Malec Engineering';
    companyCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: THEME.titleColor } };
    currentRow++;

    // Row 2: Report title
    const titleCell = ws.getCell(currentRow, 1);
    titleCell.value = config.reportName;
    titleCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: THEME.titleColor } };
    currentRow++;

    // Row 3: Date
    const dateCell = ws.getCell(currentRow, 1);
    dateCell.value = `Generated: ${new Date().toLocaleString()}`;
    dateCell.font = { name: 'Calibri', size: 9, italic: true, color: { argb: 'FF6B7280' } };
    currentRow++;

    // Row 4: Parameters summary
    const paramParts = [];
    (config.parameters || []).forEach(param => {
      const sels = parameters[param.field] || [];
      if (sels.length > 0) {
        const values = sels.map(s => s.fullDisplay || s.display).join(', ');
        paramParts.push(`${param.label}: ${values}`);
      }
    });
    if (paramParts.length > 0) {
      const paramCell = ws.getCell(currentRow, 1);
      paramCell.value = `Filters: ${paramParts.join(' | ')}`;
      paramCell.font = { name: 'Calibri', size: 9, color: { argb: 'FF6B7280' } };
    }
    currentRow++;

    // Row 5: blank spacer
    currentRow++;

    // ===== Column Headers =====
    const headerRowNum = currentRow;
    const headerRow = ws.getRow(headerRowNum);

    columns.forEach((col, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = col.label;
      cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: THEME.headerFont } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: THEME.headerFill } };
      cell.alignment = {
        horizontal: col.align || 'left',
        vertical: 'middle'
      };
      cell.border = {
        bottom: { style: 'thin', color: { argb: THEME.headerBorder } },
        right: { style: 'thin', color: { argb: THEME.headerBorder } }
      };
    });
    headerRow.height = 22;
    currentRow++;

    // Freeze panes at the header row
    ws.views = [{ state: 'frozen', ySplit: headerRowNum, xSplit: 0 }];

    // ===== Data Rows =====
    data.forEach((row, idx) => {
      const dataRow = ws.getRow(currentRow);
      const isZebra = idx % 2 === 1;

      columns.forEach((col, i) => {
        const camelField = col.field.charAt(0).toLowerCase() + col.field.slice(1);
        const displayKey = `${camelField}_Display`;
        const displayValue = row[displayKey] != null ? row[displayKey] : row[camelField];

        const cell = dataRow.getCell(i + 1);
        cell.value = displayValue != null ? displayValue : '';
        cell.font = { name: 'Calibri', size: 10 };
        cell.alignment = { horizontal: col.align || 'left', vertical: 'middle' };
        cell.border = {
          bottom: { style: 'hair', color: { argb: THEME.borderColor } },
          right: { style: 'hair', color: { argb: THEME.borderColor } }
        };

        if (isZebra) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: THEME.zebraFill } };
        }
      });

      currentRow++;
    });

    // ===== Auto-size Columns =====
    columns.forEach((col, i) => {
      const camelField = col.field.charAt(0).toLowerCase() + col.field.slice(1);
      const displayKey = `${camelField}_Display`;

      // Measure max content width
      let maxLen = col.label.length;
      data.forEach(row => {
        const val = row[displayKey] != null ? row[displayKey] : row[camelField];
        if (val != null) {
          const len = String(val).length;
          if (len > maxLen) maxLen = len;
        }
      });

      // Cap at reasonable width, minimum from label
      ws.getColumn(i + 1).width = Math.min(Math.max(maxLen + 2, 8), 50);
    });

    // ===== Generate and Download =====
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const filename = buildReportFilename(config.reportName, 'xlsx');

    // Try File System Access API
    if (window.showSaveFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: 'Excel Workbook',
            accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
          }]
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        if (onProgress) onProgress(null);
        return true;
      } catch (err) {
        if (err.name === 'AbortError') {
          if (onProgress) onProgress(null);
          return false;
        }
      }
    }

    // Fallback
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    if (onProgress) onProgress(null);
    return true;
  } catch (err) {
    console.error('[reportExcelExporter] Error:', err);
    if (onProgress) onProgress(null);
    throw err;
  }
};

export default exportReportExcel;
